import React from "react";
import "./blog-post.scss";
import { Link, graphql } from "gatsby";
import Image from "gatsby-image";
import parse from "html-react-parser";

// We're using Gutenberg so we need the block styles
// these are copied into this project due to a conflict in the postCSS
// version used by the Gatsby and @wordpress packages that causes build
// failures.
// @todo update this once @wordpress upgrades their postcss version
import "../css/@wordpress/block-library/build-style/style.css";
import "../css/@wordpress/block-library/build-style/theme.css";
import Layout from "../components/layout/layout";
import Seo from "../components/seo";
import PostCardList from "../components/postCardList/postCardList";
import Breadcrumb from "../components/breadcrumb/breadcrumb";
import FallBackImg from "../components/FallBackImg/fallBackImg";
import AccOpeningBannerDesktop from "../assets/images/acc-opening-banner-desktop.jpg";
import AccOpeningBannerMobile from "../assets/images/acc-opening-banner-mobile.jpg";
import { ACC_OPENING_BANNER } from "../constants/externalLinks";

const BlogPostTemplate = ({ data: { post, allPosts } }) => {
  const posts = allPosts.nodes.filter(
    currentPost => currentPost.title !== post.title
  );

  const postCategory = post.categories?.nodes
    ? post.categories?.nodes[0]
    : undefined;

  const breadcrumbLinks = [
    {
      uri: "/",
      name: "Home",
    },
    {
      uri: "/",
      name: "Blog",
    },
    {
      uri: post.categories?.nodes[0].uri,
      name: post.categories?.nodes[0].name,
    },
    {
      uri: "",
      name: post.title,
    },
  ];

  const featuredImage = post.featuredImage
    ? {
      fluid:
          post.featuredImage?.node?.localFile
            ?.childImageSharp?.fluid,
      alt: post.featuredImage?.node?.alt || "",
    }
    : null;

  return (
    <>
      <Layout>
        <Seo
          title={post.title || " "}
          description={post.excerpt || " "}
          pageURI={post.uri}
          image={
            featuredImage ? featuredImage.fluid?.src : null
          }
          meta={[
            {
              name: "robots",
              content: "index,follow",
            },
          ]}
        />
        <Breadcrumb breadcrumbLinks={breadcrumbLinks} />
        <div className="blog-post">
          <div className="content-left">
            <article
              className="post"
              itemScope
              itemType="http://schema.org/Article"
            >
              <header>
                {/* if we have a featured image for this post let's display it */}
                <div className="post-img-wrap">
                  {featuredImage ? (
                    <Image
                      fluid={featuredImage.fluid}
                      alt={featuredImage.alt}
                    />
                  ) : (
                    <FallBackImg />
                  )}
                </div>

                <h1
                  itemProp="headline"
                  className="post-title"
                >
                  {parse(post.title || " ")}
                </h1>
                {/* <section
                  itemProp="description"
                  className="post-description"
                >
                  {parse(post.excerpt || " ")}
                </section> */}
                <div className="post-info-wrap">
                  {postCategory ? (
                    <div className="post-info category-wrap">
                      <Link
                        to={postCategory.uri}
                        itemProp="url"
                      >
                        <div className="content">
                          Category :
                          <div className="category">
                            {postCategory.name}
                          </div>
                        </div>
                      </Link>
                    </div>
                  ) : null}

                  <div className="dot"></div>
                  <div className="post-info author-wrap">
                    By :{" "}
                    {post.author.node.name
                      ? `${post.author.node.name}`
                      : `${post.author.node.firstName} ${post.author.node.lastName}`}
                  </div>
                  <div className="dot"></div>
                  <div className="post-info date-wrap">
                    <div className="date">{post.date}</div>
                  </div>
                  {/* <div className="dot"></div>
                  <div className="post-info read-time-wrap"></div> */}
                </div>
              </header>

              {!!post.content && (
                <section
                  itemProp="articleBody"
                  className="post-content"
                >
                  {parse(post.content || " ")}
                </section>
              )}
            </article>
          </div>
          <div className="content-right">
            <a
              href={ACC_OPENING_BANNER}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={AccOpeningBannerDesktop}
                className="show-desktop"
              />
              <img
                src={AccOpeningBannerMobile}
                className="show-mobile"
              />
            </a>
          </div>
        </div>
        {posts.length > 0 ? (
          <div className="similar-posts">
            <div className="similar-posts-title">
              People also read
            </div>
            <PostCardList posts={posts} />
          </div>
        ) : null}
      </Layout>
    </>
  );
};

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $categoryId: String
    $previousPostId: String
    $nextPostId: String
  ) {
    # selecting the current post by id
    post: wpPost(id: { eq: $id }) {
      id
      excerpt
      content
      title
      uri
      date(formatString: "DD/MM/YYYY")
      tags {
        nodes {
          name
        }
      }
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
      author {
        node {
          name
          firstName
          lastName
        }
      }
      categories {
        nodes {
          id
          name
          uri
        }
      }
    }
    allPosts: allWpPost(
      filter: {
        categories: {
          nodes: { elemMatch: { id: { eq: $categoryId } } }
        }
      }
      sort: { fields: [date], order: DESC }
    ) {
      nodes {
        excerpt
        uri
        date(formatString: "DD/MM/YYYY")
        title
        excerpt
        author {
          node {
            id
            name
          }
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid_tracedSVG
                }
              }
            }
          }
        }
        categories {
          nodes {
            id
            name
            uri
          }
        }
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      title
    }

    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      title
    }
  }
`;
